import { useEffect, useRef, useState } from "react";
import useBudgetStatus from "../hooks/useBudgetStatus";
import BudgetGauge from "./BudgetGauge";
import BudgetProgressBar, { budgetStatusFor, BUDGET_STATUS } from "./BudgetProgressBar";
import { formatNumber } from "./BudgetGrid";

// Indicador circular de presupuesto, fijo en la esquina inferior izquierda,
// siempre visible sin importar la pagina o el scroll. El texto se despliega al
// pasar el cursor; un clic despliega el desglose por categoria. Solo informativo.
// El Aprobador (groupId 9) puede alternar a una vista global (todas las sucursales).
export default function BudgetFloatingGauge({ user }) {
    const isSupervisor = user?.groupId === "     9";
    const [globalView, setGlobalView] = useState(false);
    const status = useBudgetStatus(user, { global: isSupervisor && globalView });
    const [expanded, setExpanded] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        if (!expanded) return;
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setExpanded(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [expanded]);

    if (!status) return null;

    const pctLabel = formatNumber(status.pct).replace(/\.00$/, "");
    const monthLabel = new Intl.DateTimeFormat("es-EC", { month: "long" }).format(new Date());
    const sortedCategories = [...status.categories].sort((a, b) =>
        (a.categoryName || "").localeCompare(b.categoryName || "")
    );
    const overCount = sortedCategories.filter((c) => c.pct > 100 || (c.asignado <= 0 && c.consumo > 0)).length;
    const warnCount = sortedCategories.filter((c) => c.pct >= 70 && c.pct <= 100).length;
    // Consumo total en dolares (con proyeccion del carrito ya incluida en status.pct).
    const consumoTotal = (status.pct / 100) * status.asignado;

    return (
        <div ref={containerRef} className="fixed bottom-5 left-5 z-[9999]">
            {/* Panel de categorias */}
            <div
                className={`absolute bottom-full left-0 mb-3 w-72 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden transition-all duration-300 ease-out origin-bottom-left ${
                    expanded ? "opacity-100 scale-100 translate-y-0 pointer-events-auto" : "opacity-0 scale-95 translate-y-2 pointer-events-none"
                }`}
            >
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/60">
                    <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-800">Presupuesto por categoría</h4>
                        <span className="text-xs text-gray-400 capitalize">{monthLabel}</span>
                    </div>
                    {(overCount > 0 || warnCount > 0) && (
                        <p className="text-xs mt-0.5">
                            {overCount > 0 && <span className="text-red-600 font-medium">{overCount} excedida{overCount > 1 ? "s" : ""}</span>}
                            {overCount > 0 && warnCount > 0 && <span className="text-gray-400"> · </span>}
                            {warnCount > 0 && <span className="text-amber-600 font-medium">{warnCount} cerca del límite</span>}
                        </p>
                    )}
                </div>
                <div className="max-h-72 overflow-y-auto divide-y divide-gray-200">
                    {sortedCategories.map((c) => {
                        // Sin presupuesto pero con consumo real = alerta (rojo), no "ok".
                        const noBudgetSpent = c.asignado <= 0 && c.consumo > 0;
                        const rowStatus = noBudgetSpent ? BUDGET_STATUS.over : budgetStatusFor(c.pct);
                        return (
                            <div
                                key={c.categoryId}
                                className="relative flex items-center gap-3 pl-4 pr-3 py-1.5 hover:bg-gray-50 transition-colors"
                            >
                                <span className={`absolute left-0 top-0 bottom-0 w-1 ${rowStatus.bar}`} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-gray-700 truncate">{c.categoryName}</p>
                                    <p className={`text-sm font-medium tabular-nums ${rowStatus.text}`}>
                                        {formatNumber(c.consumo)} / {formatNumber(c.asignado)}
                                    </p>
                                </div>
                                <BudgetProgressBar asignado={c.asignado} pct={c.pct} consumo={c.consumo} />
                            </div>
                        );
                    })}
                </div>
                <div className="px-4 py-2.5 flex items-center justify-between bg-gray-50/60 border-t border-gray-200">
                    {isSupervisor ? (
                        <label
                            className="flex items-center gap-2 cursor-pointer select-none"
                            onClick={(e) => e.stopPropagation()}
                            title={globalView ? "Viendo todas las sucursales" : "Viendo solo tu sucursal"}
                        >
                            <span className="text-xs font-semibold text-gray-600">Global</span>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={globalView}
                                onClick={() => setGlobalView((v) => !v)}
                                className={`relative inline-flex items-center h-5 w-9 rounded-full flex-shrink-0 transition-colors ${
                                    globalView ? "bg-brand-primary" : "bg-gray-300"
                                }`}
                            >
                                <span
                                    className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                                        globalView ? "translate-x-4" : "translate-x-0"
                                    }`}
                                />
                            </button>
                        </label>
                    ) : (
                        <span className="text-xs font-semibold text-gray-600">Total</span>
                    )}
                    <span className="text-sm font-bold text-gray-900 tabular-nums">
                        {formatNumber(consumoTotal)} / {formatNumber(status.asignado)}
                    </span>
                </div>
            </div>

            {/* Boton flotante */}
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="group relative flex items-center bg-white rounded-full shadow-lg border border-gray-200 p-2 hover:pr-5 transition-all duration-300 ease-out"
                title={`Presupuesto ${globalView ? "global (todas las sucursales)" : "de tu sucursal"} — ${monthLabel}: ${pctLabel}% consumido este mes`}
            >
                <BudgetGauge asignado={status.asignado} pct={status.pct} size={76} strokeWidth={7} labelClass="text-sm" />
                <span className="max-w-0 group-hover:max-w-[140px] group-hover:ml-3 overflow-hidden whitespace-nowrap text-sm font-semibold text-gray-600 transition-all duration-300 ease-out">
                    Presupuesto del mes
                </span>
            </button>
        </div>
    );
}
